import clsx from "clsx";
import { isEmpty } from "lodash";
import {
  Card,
  KeyValueList,
  RenderWhen,
  ThreatStatusLabel,
  Time
} from "../../../../../../components";
import { AuxiliaryAnalysis } from "../../../../@types";
import { SECTIONS } from "../../../../consts";
import { getRlAuxiliaryAnalysisClassification } from "../../../analysistable/rl_analysis_table/rl_auxiliary_analysis/utils";
import { CardSubtitle } from "../../components";

import {
  useGetSampleAnalysisStatus,
  useLocalSampleSummaryParams,
  useReanalyze
} from "../../../../hooks";
import styles from "../analysis-card.module.scss";

type Props = {
  data?: AuxiliaryAnalysis;
  onClick: (section: string) => void;
};

const AuxiliaryAnalysis = ({ data, onClick }: Props) => {
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();
  const { mutate: reanalyze } = useReanalyze();
  const {
    data: {
      dynamic_analysis: { rl_auxiliary_analysis: analysis = undefined } = {}
    } = {},
    refetch
  } = useGetSampleAnalysisStatus(hash);

  const handleClick = () => {
    onClick(SECTIONS.AUXILIARY_ANALYSIS.INDEX);
  };

  const handleAnalyze = () => {
    if (!sourceId) return;
    reanalyze({
      samples: [
        {
          hash_value: hash,
          source_id: sourceId,
          relationship_id: relationshipId
        }
      ],
      integrations: ["rl_auxiliary_analysis"]
    });
    refetch();
  };

  return (
    <Card
      disabled={isEmpty(data)}
      icon="auxiliary-analysis"
      title="Auxiliary Analysis"
      subtitle={
        <CardSubtitle
          additionalInfo={analysis?.additional_information}
          displayStatus={analysis?.display_status}
          hasData={!isEmpty(data)}
          type="INTEGRATION"
          onAnalyze={handleAnalyze}
        >
          <RenderWhen condition={data?.max_score !== undefined}>
            <ThreatStatusLabel
              className={styles.analysis_card_subtitle_classification}
              threatStatus={getRlAuxiliaryAnalysisClassification(
                data?.max_score ?? 0
              )}
              showOrb={false}
              showColor
            />
          </RenderWhen>
        </CardSubtitle>
      }
      data-testid="report_summary_auxiliary_analysis"
    >
      <KeyValueList
        className={styles.analysis_card_content_wrapper}
        onClick={handleClick}
        data-testid="report_summary_auxiliary_analysis_list"
      >
        <KeyValueList.Item
          className={styles.list_item_auxiliary_analysis}
          keyClassName={clsx("font-medium", "white-space-nowrap")}
          label="Analysis Time: "
          value={
            <Time
              className={clsx(styles.time, "overflow-ellipsis")}
              time={data?.completed}
            />
          }
          data-testid="report_summary_auxiliary_analysis_analysis_time"
        />
        <KeyValueList.Item
          className={styles.list_item_auxiliary_analysis}
          keyClassName={clsx("font-medium", "white-space-nowrap")}
          label="Threat Score: "
          value={
            <div className={styles.list_item_value}>{data?.max_score}</div>
          }
          data-testid="report_summary_auxiliary_analysis_threat_score"
        />
        <KeyValueList.Item
          className={styles.list_item_auxiliary_analysis}
          keyClassName={clsx("font-medium", "white-space-nowrap")}
          label="File Count: "
          value={
            <div className={styles.list_item_value}>{data?.file_count}</div>
          }
          data-testid="report_summary_auxiliary_analysis_file_count"
        />
      </KeyValueList>
    </Card>
  );
};

export default AuxiliaryAnalysis;
